import { QUOTATION_STATUSES, type QuotationStatus } from '@navicosa/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import {
  QUOTE_STATUS_COLOR,
  QUOTE_STATUS_LABEL,
  type QuotationListItem,
  vnd,
} from '../../api/types';

/** Danh sách báo giá (PRD 6.5). Sale chỉ thấy báo giá của yêu cầu được phân công – máy chủ tự lọc. */
export function QuotationListPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const page = Number(sp.get('page') ?? 1);
  const status = (sp.get('status') as QuotationStatus | null) ?? undefined;
  const set = (k: string, v?: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'page') next.delete('page');
    setSp(next);
  };
  const list = useQuery({
    queryKey: ['quotations', page, status, sp.get('q')],
    queryFn: () =>
      api.page<QuotationListItem>('/admin/quotations', { page, limit: 20, status, q: sp.get('q') }),
  });
  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Báo giá
        </Typography.Title>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Mã BG/YC, tên khách, công ty"
          allowClear
          defaultValue={sp.get('q') ?? ''}
          onSearch={(v) => set('q', v.trim() || undefined)}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          placeholder="Trạng thái"
          style={{ width: 180 }}
          value={status}
          onChange={(v) => set('status', v)}
          options={QUOTATION_STATUSES.map((s) => ({ value: s, label: QUOTE_STATUS_LABEL[s] }))}
        />
      </Space>
      <Table<QuotationListItem>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.meta.total,
          onChange: (p) => set('page', String(p)),
          showTotal: (t) => `${t} báo giá`,
        }}
        onRow={(r) => ({ onClick: () => nav(`/quotations/${r.id}`), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'Chưa có báo giá. Lập báo giá từ trang chi tiết yêu cầu thuê.' }}
        columns={[
          {
            title: 'Mã',
            render: (_, r) => (
              <Link to={`/quotations/${r.id}`} onClick={(e) => e.stopPropagation()}>
                {r.quote_code}
              </Link>
            ),
          },
          { title: 'Yêu cầu', render: (_, r) => r.request.request_code },
          {
            title: 'Khách',
            render: (_, r) =>
              [r.request.contact_company, r.request.contact_name].filter(Boolean).join(' – '),
          },
          { title: 'Tổng cộng', align: 'right', render: (_, r) => vnd(r.total_amount) },
          {
            title: 'Hiệu lực đến',
            render: (_, r) => (r.valid_until ? dayjs(r.valid_until).format('DD/MM/YYYY') : '—'),
          },
          { title: 'Phụ trách', render: (_, r) => r.request.assigned_user?.full_name ?? '—' },
          {
            title: 'Trạng thái',
            render: (_, r) => (
              <Space size={4}>
                <Tag color={QUOTE_STATUS_COLOR[r.status]}>{QUOTE_STATUS_LABEL[r.status]}</Tag>
                {r.booking && <Tag color="green">{r.booking.booking_code}</Tag>}
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );
}
